import { combinePath } from '@ms/yammer-web-support/dist/location';
import { matchPath } from 'react-router-dom';

import type { PageRoute, ParentPageRoute } from './AppPageRoutesWithTelemetry.types';

/**
 * Synchronously resolves the child route of a parent route that matches the current pathname.
 *
 * A parent route loads its page bundle lazily, so the child route's own telemetry effect — which starts
 * the page-load scenario — does not run until that bundle resolves. That excludes cold bundle latency
 * from the measurement. Resolving the matched child here, at the synchronous parent-match layer outside
 * the lazy boundary, lets the scenario clock start at route match so bundle load time is captured.
 *
 * The returned route mirrors the shape the child route element receives (child path combined with the
 * parent path, same `hero` array reference), so the child's later route commit compares equal and is a
 * no-op. Returns `undefined` when no child matches (e.g. a fallback redirect handles the URL).
 */
export const resolveMatchedChildRoute = (parentRoute: ParentPageRoute, pathname: string): PageRoute | undefined => {
  for (const childRoute of parentRoute.childRoutes) {
    const path = combinePath(parentRoute.path, childRoute.path);
    const match = matchPath(pathname, { path, exact: childRoute.exact });

    if (match) {
      return { ...childRoute, path };
    }
  }

  return undefined;
};
